import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api.service';
import { AuthResponse, User, UserRole } from '../types/user';
import { AuthProviderInfo, LoginDto, SignupDto, UpdateUserDto } from '../types/auth';

// Define what functions and data our Auth Context will provide to components
// This is like a "contract" - any component using useAuth() will get these properties
interface AuthContextType {
    user: AuthResponse | null;                                    // Current logged-in user (null if not logged in)
    loading: boolean;                                     // True when checking if user is already logged in on app start
    login: (email: string, password: string) => Promise<void>;  // Function to log user in
    logout: () => Promise<void>;                                   // Function to log user out
    signup: (signupDto: SignupDto) => Promise<void>; // Function to sign up
    update: (updateUserDto: UpdateUserDto) => Promise<void>;            // Function to update user
    loginWithGoogle: () => Promise<void>;
    signupWithGoogle: () => Promise<void>;
    getUserProviderInfo: () => Promise<AuthProviderInfo>;
    isAuthenticated: boolean;                             // Quick way to check if user is logged in
    canJoinActivities: boolean;
    isUser: boolean;
    isCreator: boolean;
    isAdmin: boolean;
}

// Create the Context - this is like creating a "container" that can hold our auth data
// We pass 'undefined' as default because we'll always wrap our app with AuthProvider
// The <AuthContextType | undefined> tells TypeScript what type of data this context holds
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Custom hook to use our Auth Context
// This is a helper function that makes it easier and safer to access our auth data
export const useAuth = () => {
    // useContext(AuthContext) gets the current value from the nearest AuthProvider up the tree
    const context = useContext(AuthContext);

    // Safety check: if someone tries to use useAuth() outside of AuthProvider, throw an error
    // This prevents bugs and gives a clear error message
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    // Return the context data (user, login, logout, etc.)
    return context;
};

// Props interface for our AuthProvider component
// ReactNode means it can contain any valid React content (components, text, etc.)
interface AuthProviderProps {
    children: ReactNode;  // All the components that will be wrapped by this provider
}

// The AuthProvider component - this wraps our entire app and provides auth state to all children
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    // State to hold the current user (null means no one is logged in)
    const [user, setUser] = useState<AuthResponse | null>(null);
    const isUser = user?.role === UserRole.USER;
    const isCreator = user?.role === UserRole.CREATOR;
    const isAdmin = user?.role === UserRole.ADMIN;

    // State to track if we're still checking if user is already logged in
    // This prevents the app from showing "not logged in" before we've checked localStorage
    const [loading, setLoading] = useState(true);
    const canJoinActivities = user?.canJoinActivities || false;

    // Function to validate the session
    const checkAuth = async () => {
        setLoading(true);
        try {
            const currentUser = await apiService.getCurrentUser();
            setUser(currentUser);
        } catch (error) {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }

    // useEffect runs when the component first mounts (app starts)
    // This checks if the user was previously logged in by looking at localStorage
    useEffect(() => {
        checkAuth();
    }, []); // Empty dependency array means this only runs once when component mounts

    // Function to handle user login
    const login = async (email: string, password: string) => {
        try {
            setLoading(true);

            // Call our API service to authenticate the user
            const userData = await apiService.login({ email, password });

            // Update our React state with the user data
            // This will cause all components using useAuth() to re-render with the new user
            setUser(userData as AuthResponse);
            setLoading(false);
        } catch (error) {
            // Re-throw the error so the component calling login() can handle it
            // (e.g., show an error message to the user)
            setLoading(false);
            throw error;
        }
    };

    const update = async (updateUserDto: UpdateUserDto) => {
        try {
            const userData = await apiService.updateUser(updateUserDto);
            setUser(userData as AuthResponse);
        } catch (error) {
            // set loading to false to let the user adjust the form
            setLoading(false);
            throw error;
        }
    }

    // Function to handle user logout
    const logout = async () => {
        // Call our API service to logout the user
        await apiService.logout();

        // Update React state to indicate no user is logged in
        // This will cause all components using useAuth() to re-render and hide auth-only content
        setUser(null);
    };

    const signup = async (signupDto: SignupDto) => {
        try {
            setLoading(true);
            const userData = await apiService.signup(signupDto);
            setUser(userData as AuthResponse);
            setLoading(false);
        } catch (error) {
            // set loading to false to let the user adjust the form
            // in case of error, the user can update form(email etc.) and try again
            setLoading(false);
            // re-throw the error so the component calling signup() can handle it
            // (e.g., toast.error(error.message))
            throw error;
        }
    }

    const loginWithGoogle = async () => {
        try {
            await apiService.loginWithGoogle();
            // user will be redirected to google auth page
            // no need to update state here
        } catch (error) {
            throw error;
        }
    }

    const signupWithGoogle = async () => {
        try {
            await apiService.signupWithGoogle();
            // user will be redirected to google auth page
            // no need to update state here
        } catch (error) {
            throw error;
        }
    }

    const getUserProviderInfo = async () => {
        try {
            return await apiService.getUserProviderInfo();
        } catch (error) {
            console.error('Error getting provider info:', error);
            return { provider: 'email', canSwitchProvider: false, email: '' };
        }
    };

    // Computed value: user is authenticated if we have a user object
    // !! converts the value to a boolean (null becomes false, any object becomes true)
    const isAuthenticated = !!user;

    // Render the Provider component with our auth data
    // The value prop contains all the data/functions that child components can access
    return (
        <AuthContext.Provider value={{
            user,              // Current user object or null
            loading,           // Whether we're still checking initial auth status
            login,             // Function to log in
            logout,            // Function to log out
            signup,            // Function to sign up
            update,            // Function to update user
            loginWithGoogle,   // Function to log in with Google
            signupWithGoogle,  // Function to sign up with Google
            getUserProviderInfo, // Function to get user provider info
            isAuthenticated,   // Boolean indicating if user is logged in
            canJoinActivities, // Boolean indicating if user can join activities
            isUser,            // Boolean indicating if user is a user
            isCreator,        // Boolean indicating if user is an creator
            isAdmin,          // Boolean indicating if user is an admin
        }}>
            {children}         {/* Render all child components inside the provider */}
        </AuthContext.Provider>
    );
};
